import { useGeckoPermissions } from 'features/permissions'
import { X } from 'lucide-react'
import React from 'react'
import { createDefaultEntry, Form781AData, FormEntry, SymbolType } from 'types'

import {
  CellCheckbox,
  CellInput,
  CellLabel,
  CellTextarea,
  Checkbox,
  CheckboxSublabel,
  ClearButton,
  DateInputWrapper,
  EmptyFormState,
  EmptyStateIcon,
  EmptyStateSubtext,
  FormCell,
  FormCellNarrow,
  FormContent,
  FormHeader,
  FormPanel,
  FormRow,
  FormTable,
  FormTitle,
  SplitCell,
  SplitCellContent,
  SplitCellHeader,
  SplitRow,
  SymbolButton,
  SymbolButtonGroup,
} from './DiscrepancyForm.styles'

interface DiscrepancyFormProps {
  entryIndex: number | null
  formData: Form781AData
  onFormChange: (data: Form781AData) => void
}

const SYMBOL_OPTIONS: { value: SymbolType; display: string }[] = [
  { value: 'red-x', display: '✕' },
  { value: 'red-dash', display: '—' },
  { value: 'red-diagonal', display: '/' },
]

export const DiscrepancyForm: React.FC<DiscrepancyFormProps> = ({
  entryIndex,
  formData,
  onFormChange,
}) => {
  const { user } = useGeckoPermissions()

  if (entryIndex === null) {
    return (
      <FormPanel>
        <EmptyFormState>
          <EmptyStateIcon>📋</EmptyStateIcon>
          <div>Select an entry from the list</div>
          <EmptyStateSubtext>
            to view and edit its form fields
          </EmptyStateSubtext>
        </EmptyFormState>
      </FormPanel>
    )
  }

  const existingEntry = formData.entries[entryIndex]
  const entry = existingEntry || createDefaultEntry()

  const updateField = <K extends keyof FormEntry>(
    field: K,
    value: FormEntry[K]
  ) => {
    const updatedEntry = { ...entry, [field]: value }
    const newEntries = [...formData.entries]
    newEntries[entryIndex] = updatedEntry
    onFormChange({
      ...formData,
      entries: newEntries,
    })
  }

  const entryTitle = entry.annotationLabel || `Entry ${entryIndex + 1}`

  return (
    <FormPanel>
      <FormHeader>
        <div>
          <FormTitle>{entryTitle}</FormTitle>
        </div>
      </FormHeader>

      <FormContent>
        <FormTable>
          <FormRow>
            <FormCell>
              <CellLabel>SYM</CellLabel>
              <SymbolButtonGroup>
                {SYMBOL_OPTIONS.map((opt) => (
                  <SymbolButton
                    key={opt.value}
                    type='button'
                    $selected={entry.sym === opt.value}
                    onClick={() =>
                      updateField(
                        'sym',
                        entry.sym === opt.value ? '' : opt.value
                      )
                    }>
                    {opt.display}
                  </SymbolButton>
                ))}
              </SymbolButtonGroup>
            </FormCell>

            <FormCell>
              <CellLabel>JCN</CellLabel>
              <CellInput
                type='text'
                value={entry.jcn}
                onChange={(e) => updateField('jcn', e.target.value)}
                placeholder='Enter'
              />
            </FormCell>

            <FormCell>
              <CellLabel>DATE DISC</CellLabel>
              <CellInput
                type='date'
                value={entry.dateDisc}
                onChange={(e) => updateField('dateDisc', e.target.value)}
              />
            </FormCell>

            <FormCell>
              <CellLabel>DOC NO.</CellLabel>
              <CellInput
                type='text'
                value={entry.docNo}
                onChange={(e) => updateField('docNo', e.target.value)}
                placeholder='Enter'
              />
            </FormCell>

            <FormCellNarrow>
              <CellCheckbox>
                <CellLabel>CF</CellLabel>
                <Checkbox
                  type='checkbox'
                  checked={entry.cf}
                  onChange={(e) => updateField('cf', e.target.checked)}
                />
                <CheckboxSublabel>781A</CheckboxSublabel>
              </CellCheckbox>
            </FormCellNarrow>

            <FormCellNarrow>
              <CellCheckbox>
                <CellLabel>XF</CellLabel>
                <Checkbox
                  type='checkbox'
                  checked={entry.xf}
                  onChange={(e) => updateField('xf', e.target.checked)}
                />
                <CheckboxSublabel>781K</CheckboxSublabel>
              </CellCheckbox>
            </FormCellNarrow>

            <FormCell>
              <CellLabel>DATE CORRECTED</CellLabel>
              <DateInputWrapper>
                <CellInput
                  type='date'
                  value={entry.dateCorrected}
                  onChange={(e) => updateField('dateCorrected', e.target.value)}
                />
                {entry.dateCorrected && (
                  <ClearButton
                    type='button'
                    onClick={() => updateField('dateCorrected', '')}
                    title='Clear date'>
                    <X />
                  </ClearButton>
                )}
              </DateInputWrapper>
            </FormCell>
          </FormRow>

          <SplitRow $minHeight='200px'>
            <SplitCell>
              <FormRow>
                <FormCell>
                  <CellLabel>WUC/REF DESIGNATOR</CellLabel>
                  <CellInput
                    type='text'
                    value={entry.wucRef}
                    onChange={(e) => updateField('wucRef', e.target.value)}
                    placeholder='Enter'
                  />
                </FormCell>
                <FormCell>
                  <CellLabel>FAULT CODE</CellLabel>
                  <CellInput
                    type='text'
                    value={entry.faultCode}
                    onChange={(e) => updateField('faultCode', e.target.value)}
                    placeholder='Enter'
                  />
                </FormCell>
                <FormCell $noBorder>
                  <CellLabel>STA CODE</CellLabel>
                  <CellInput
                    type='text'
                    value={entry.staCode}
                    onChange={(e) => updateField('staCode', e.target.value)}
                    placeholder='Enter'
                  />
                </FormCell>
              </FormRow>

              <SplitCellHeader>DISCREPANCY</SplitCellHeader>
              <SplitCellContent>
                <CellTextarea
                  value={entry.discrepancyText}
                  onChange={(e) =>
                    updateField('discrepancyText', e.target.value)
                  }
                  placeholder='Enter discrepancy details...'
                />
              </SplitCellContent>
            </SplitCell>

            <SplitCell>
              <SplitCellHeader>CORRECTIVE ACTION</SplitCellHeader>
              <SplitCellContent>
                <CellTextarea
                  value={entry.correctiveAction}
                  onChange={(e) =>
                    updateField('correctiveAction', e.target.value)
                  }
                  placeholder='Enter corrective action...'
                />
              </SplitCellContent>

              <FormRow $noBorder>
                <FormCell>
                  <CellLabel>CORRECTED BY</CellLabel>
                  <CellInput
                    type='text'
                    value={entry.correctedBy}
                    onChange={(e) => updateField('correctedBy', e.target.value)}
                    placeholder='Enter name'
                  />
                </FormCell>
                <FormCell>
                  <CellLabel>EMPLOYEE NO.</CellLabel>
                  <CellInput
                    type='text'
                    value={entry.correctedByEmployeeNo}
                    onChange={(e) =>
                      updateField('correctedByEmployeeNo', e.target.value)
                    }
                    placeholder='Enter'
                  />
                </FormCell>
              </FormRow>
            </SplitCell>
          </SplitRow>

          <FormRow>
            <FormCell>
              <CellLabel>DISCOVERED BY (Print)</CellLabel>
              <CellInput
                type='text'
                value={entry.discoveredBy}
                onChange={(e) => updateField('discoveredBy', e.target.value)}
                placeholder='Enter name'
              />
            </FormCell>

            <FormCell>
              <CellLabel>EMPLOYEE NO.</CellLabel>
              <CellInput
                type='text'
                value={entry.discoveredByEmployeeNo}
                onChange={(e) =>
                  updateField('discoveredByEmployeeNo', e.target.value)
                }
                placeholder='Enter'
              />
            </FormCell>

            <FormCell>
              <CellLabel>INSPECTED BY</CellLabel>
              <CellInput type='text' value={user?.email || ''} disabled />
            </FormCell>

            <FormCell>
              <CellLabel>EMPLOYEE NO.</CellLabel>
              <CellInput
                type='text'
                value={entry.inspectedByEmployeeNo}
                onChange={(e) =>
                  updateField('inspectedByEmployeeNo', e.target.value)
                }
                placeholder='Enter'
              />
            </FormCell>
          </FormRow>
        </FormTable>
      </FormContent>
    </FormPanel>
  )
}
